import { useCallback, useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';

import { getAllAudioAssets } from '@dyl/apiClients/backendApi';
import createDatabaseManager from '@dyl/utils/createDatabaseManager';
import createSnackbarAction from '@dyl/utils/createSnackbarAction';
import { Refresh as RefreshIcon } from '@mui/icons-material';

const { Provider: AudiosAssetsManager, use: useRaw } = createDatabaseManager({
    requestHook: function useAudiosRequest() {
        const { enqueueSnackbar } = useSnackbar();

        return useCallback(async (args = {}) => {
          const { page = 1, perPage = 10, orderBy = 'audio_id', order = 'ASC'  } = args;
          while (true) {
                try {
                    return await getAllAudioAssets({
                        type: 'ITEM',
                        search: args.search || undefined,
                        orderBy: orderBy || 'audio_id',
                        order: order || 'ASC',
                        page: page || 1,
                        perPage: perPage || 10,
                        version: '1'
                    });
              }catch {
                await new Promise(resolve =>
                  enqueueSnackbar('Failed to load audios', {
                    variant: 'error',
                    persist: true,
                    action: createSnackbarAction({
                      iconComponent: RefreshIcon,
                      onClick: resolve,
                    }),
                  }));
              }
            }
          }, [enqueueSnackbar]);
        },
        defaultValue: [],
        contextName: 'AudiosAssetsManager',
      });

// Chose to replicate the AudiosAssetsManager as the backend won't provide all audios together
// And we'd prefer to persist the item audios for all task pages to function seamlessly
const { Provider: MotivationalAudiosAssetsManager, use: useMotivationalRaw } = createDatabaseManager({
    requestHook: function useAudiosRequest() {
        const { enqueueSnackbar } = useSnackbar();

     return useCallback(async (args = {}) => {
          const { page = 1, perPage = 10, orderBy = 'audio_id', order = 'ASC'  } = args;
       while (true) {
         try {
           return await getAllAudioAssets({
             type: 'MOTIVATIONAL',
             search: args.search || undefined,
             orderBy: orderBy || 'audio_id',
             order: order || 'ASC',
             page: page || 1,
             perPage: perPage || 10
           });
         }
         catch {
          await new Promise(resolve => enqueueSnackbar('Failed to load audios', {
            variant: 'error',
            persist: true,
            action: createSnackbarAction({
              iconComponent: RefreshIcon,
              onClick: resolve,
            }),
          }));
        }
    }
      }, []);
  },
  defaultValue: [],
  contextName: 'MotivationalAudiosAssetsManager',
});

export { AudiosAssetsManager, MotivationalAudiosAssetsManager };

export function useAudios(page, search = '', type = 'ITEM', perPage = 10, sortModel = [{ field: 'audio_id', sort: 'asc' }], auto = true) {
    const {
        data: audios,
        status: audiosStatus,
        refresh: requestAudios,
    } = useRaw();

    const {
        data: audiosMotivational,
        status: audiosMotivationalStatus,
        refresh: requestMotivationalAudios,
    } = useMotivationalRaw();

      useEffect(() => {
    if (!auto) return;
    if (type !== 'ITEM') return;
    if (typeof page !== 'number') return;
    const params = {
      search: search || undefined,
      perPage,
      page: page + 1,
      orderBy: sortModel[0]?.field || 'audio_id',
      order: (sortModel[0]?.sort || 'asc').toUpperCase()
    };
    requestAudios(params);
  }, [auto, type, search, page, perPage, sortModel, requestAudios]);

  useEffect(() => {
    if (!auto) return;
    if (type !== 'MOTIVATIONAL') return;
    if (typeof page !== 'number') return;
    const params = {
      search: search || undefined,
      perPage,
      page: page + 1,
      orderBy: sortModel[0]?.field || 'audio_id',
      order: (sortModel[0]?.sort || 'asc').toUpperCase()
    };
    requestMotivationalAudios(params);
  }, [auto, type, search, page, perPage, sortModel, requestMotivationalAudios]);

  const refreshAudios = useCallback(() => {
    if (type === 'ITEM') {
      return requestAudios({
          search: search || undefined,
          page: page + 1,
          perPage: perPage || 10,
          orderBy: sortModel[0]?.field || 'audio_id',
          order: (sortModel[0]?.sort || 'asc').toUpperCase()
        });
      } else {
        return requestMotivationalAudios({
          type: 'MOTIVATIONAL',
          search: search || undefined,
          perPage,
          page: page + 1,
          orderBy: sortModel[0]?.field || 'audio_id',
          order: (sortModel[0]?.sort || 'asc').toUpperCase()
         });
      }
    }, [requestAudios, type, search, page, perPage, sortModel, requestMotivationalAudios]);

  if (type === 'ITEM') {
    const audiosArray = Array.isArray(audios)
      ? audios
      : audios?.audios || [];

    const countFiltered = Array.isArray(audios)
      ? audios.length
      : audios?.countFiltered || 0;

    return {
      audios: audiosArray,
      countFiltered,
      audiosStatus,
      refreshAudios,
    };
  } else {
    const audiosArray = Array.isArray(audiosMotivational)
      ? audiosMotivational
      : audiosMotivational?.audios ||
      audiosMotivational?.existing_asset_list || [];

    const countFiltered = Array.isArray(audiosMotivational)
      ? audiosMotivational.length
      : audiosMotivational?.countFiltered ||
      audiosMotivational?.existing_asset_list?.length || 0;

    return {
      audios: audiosArray,
      countFiltered,
      audiosStatus: audiosMotivationalStatus,
      refreshAudios,
    };
  }
}

export function useAudio(audioId) {
    const { audios, audiosStatus } = useAudios();

    return useMemo(() => {
        if (audiosStatus !== 'complete') return null;
        if (!audioId) return null;

        const audio = audios.find(a => a.audio_id === audioId);

        return audio ?? null;
    }, [audioId, audios, audiosStatus]);
}